import React, { useCallback, useEffect } from 'react'
import { Images } from '@verzekeringapp-mono/shared/src/assets'
import LoginScreenWrapper from '~/components/screen_wrappers/login_screen_wrapper'
import { checkPasswords } from '@verzekeringapp-mono/shared/src/utils/password_utils'
import { useNavigate } from 'react-router-dom'
import useAuthentication from '@verzekeringapp-mono/shared/src/hooks/use_authentication'
import Button from '~/components/Button'
import Modal from '~/components/Modal'

interface FormData {
  password: string
  passwordRepeat: string
}

const ForgotPasswordVerifyScreen: React.FC = () => {
  const [formData, setFormData] = React.useState<FormData>({
    password: '',
    passwordRepeat: ''
  })
  const invalidLabelCSSClass = formData.password !== '' ? 'labelError' : 'label'
  const result = checkPasswords(formData.password, formData.passwordRepeat)
  const token =
    new URLSearchParams(location.search).get('token') ?? ''
  const email =
    new URLSearchParams(location.search).get('email') ?? ''
  const [error, setError] = React.useState<string | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null)

  const { forgotPasswordVerify } = useAuthentication()
  const navigate = useNavigate()

  useEffect(() => {
    if (token === '') {
      navigate('/home')
    }
  }, [token])

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target

      setFormData(formData => ({ ...formData, [name]: value }))
    },
    [formData]
  )

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      setError(null)

      try {
        setIsLoading(true)
        const result = await forgotPasswordVerify(
          email,
          formData.password,
          formData.passwordRepeat,
          token
        )

        setSuccessMessage(result.message ?? 'Je wachtwoord is succesvol aangepast.')
      } catch (error) {
        setSuccessMessage('')
        setError((error as Error).message)
      } finally {
        setIsLoading(false)
      }
    },
    [formData, token]
  )

  const onModalClosed = useCallback(() => {
    setSuccessMessage(null)
    navigate('/home')
  }, [navigate])

  return (
    <LoginScreenWrapper>
      <img
        src={Images.Logo}
        width={180}
        height={70}
      />

      <p className='title-medium mt-8'>Wachtwoord herstellen</p>

      <p className='description mt-4'>
        Hieronder kun je je nieuwe wachtwoord invullen:
      </p>

      <p className='mt-4 mb-2'>
        Bedenk een veilig wachtwoord dat voldoet aan de volgende eisen:
      </p>

      <p className={result.validLength ? 'labelSuccess' : invalidLabelCSSClass}>
        • Minimaal 8 karakters
      </p>

      <p
        className={
          result.validUppercase ? 'labelSuccess' : invalidLabelCSSClass
        }
      >
        • Minimaal 1 hoofdletter
      </p>

      <p
        className={
          result.validLowercase ? 'labelSuccess' : invalidLabelCSSClass
        }
      >
        • Minimaal 1 kleine letter
      </p>

      <p className={result.validDigit ? 'labelSuccess' : invalidLabelCSSClass}>
        • Minimaal 1 cijfer
      </p>

      <p
        className={
          result.validSpecialChar ? 'labelSuccess' : invalidLabelCSSClass
        }
      >
        • Minimaal 1 speciaal teken
      </p>

      <p
        className={
          formData.password === formData.passwordRepeat &&
          formData.password !== ''
            ? 'labelSuccess'
            : invalidLabelCSSClass
        }
      >
        • Wachtwoorden zijn hetzelfde
      </p>

      <form
        onSubmit={handleSubmit}
        className='w-full mt-8'
      >
        <label className='label'>Wachtwoord</label>

        <input
          className='textfield mt-2 mb-4'
          type='password'
          placeholder='Nieuw wachtwoord'
          name='password'
          value={formData.password}
          onChange={handleInputChange}
        />

        <label className='label'>Herhaal wachtwoord</label>

        <input
          className='textfield mt-2'
          type='password'
          placeholder='Herhaal nieuw wachtwoord'
          name='passwordRepeat'
          value={formData.passwordRepeat}
          onChange={handleInputChange}
        />

        {error && (
          <span
            className='labelError mt-4 whitespace-pre-line'
            dangerouslySetInnerHTML={{ __html: error }}
          />
        )}

        <Button
          className={'primary-btn w-full my-5'}
          type='submit'
          disabled={result.invalid}
          isLoading={isLoading}
        >
          Aanpassen
        </Button>
      </form>

      {
        successMessage && (
          <Modal onClose={onModalClosed}>
            <p>
              {successMessage}
            </p>
          </Modal>
        )
      }
    </LoginScreenWrapper>
  )
}

export default ForgotPasswordVerifyScreen
